// ============================================================
// BRAND CONFIGURATION — edit this file to update all content
// ============================================================

/** Visitor tracks. The site filters work + services by the selected track. */
export type Audience = "all" | "scaleup" | "founder";

/** Lower sorts first. Every service/project carries one rank per track. */
export type Rank = Record<Audience, number>;

export const audiences: { id: Audience; label: string; blurb: string }[] = [
  { id: "all", label: "Everything", blurb: "All of it" },
  {
    id: "scaleup",
    label: "For scale-ups",
    blurb: "Agents, RAG and infra dropped into a stack that already has users",
  },
  {
    id: "founder",
    label: "For founders",
    blurb: "0 → 1 AI products taken from idea to something people pay for",
  },
];

export const brand = {
  agency: {
    name: "ZinifyLabs",
    legalName: "MB Zinify Labs",
    tagline: "We build AI-powered products that scale.",
    // Rotating keyword in the hero headline: "We build AI-powered <word> that scale."
    heroWords: ["products", "agents", "pipelines", "platforms"],
    description:
      "Agentic systems, RAG and AI products taken from first prototype to production traffic — and kept alive there. You get running code, evals and monitoring, not a slide deck.",
    // Above-the-fold micro case study (P4). Must stay factually true.
    proofLine:
      "We funded, built and scaled our own agent to 3,000 users and €1k MRR before we ever billed a client for one.",
    email: "augustinaszin@gmail.com",
    website: "https://zinifylabs.com",
    ctaPrimary: "Start a project",
    ctaSecondary: "See our work",
    // Geography framed as a buyer benefit, not a map pin.
    location: "Vilnius, Lithuania",
    region: "EU-based",
    compliance: "GDPR-ready",
    timezone: "Europe/Vilnius",
    timezoneLabel: "CET/EET",
    coverage:
      "EU-based and GDPR-ready. We overlap the full European and UK working day, and mornings on the US East Coast.",
  },

  // Credentials rendered as a compact pill row above the fold (P4).
  credentials: ["IEEE published", "MSc Embedded Systems", "BEng, First Class"],

  // Hero proof row: live systems a visitor can open and check, instead of
  // counters. Each item names a project below, which supplies link and logo.
  liveProof: {
    label: "Live in production",
    items: [
      { project: "OpGate.ai", what: "We built its supplier-risk AI engine", logoHeight: 22 },
      { project: "Tommy", what: "Team knowledge assistant with cited answers", logoHeight: 30 },
    ],
  },

  // ⚠️ ILLUSTRATIVE — these are representative runs, not telemetry.
  // Timings, token counts and costs are plausible figures for this class of
  // system, NOT measurements from a production deployment. The UI labels them
  // as example runs. Flip `proof.measured` to true only when every number
  // below has been replaced with a real, defensible one.
  // kinds: goal · think · tool · ok · warn · answer · src
  heroDemo: [
    {
      title: "procurement-agent",
      graph: "supplier_risk_graph",
      lines: [
        { kind: "goal", text: "Find a safer supplier for PCB-2201" },
        { kind: "think", text: "Plan: check alternatives, score risk, compare lead times", ms: 412, tokens: 980, cost: 0.0041, model: "claude-sonnet" },
        { kind: "tool", text: "search_suppliers(component=\"PCB-2201\", countries=[LT, DE])", ms: 1240, tokens: 1420, cost: 0.0059 },
        { kind: "ok", text: "3 candidates found", depth: 1 },
        { kind: "tool", text: "risk_score(suppliers=3)", ms: 880, tokens: 2100, cost: 0.0087 },
        { kind: "ok", text: "compliance clean · 1 sole-source flagged", depth: 1 },
        { kind: "answer", text: "Recommend Supplier B — lead time 12 days, risk 2/5, no sanctions match.", ms: 760, tokens: 1180, cost: 0.0094, model: "claude-sonnet" },
        { kind: "src", text: "supplier registry · OTIF history · 2 web citations", depth: 1 },
      ],
    },
    {
      title: "knowledge-assistant",
      graph: "grounded_rag_graph",
      lines: [
        { kind: "goal", text: "What did we agree with Nord Logistics on pricing?" },
        { kind: "think", text: "Plan: search meeting notes, chat and shared drives", ms: 305, tokens: 640, cost: 0.0026, model: "gpt-4o-mini" },
        { kind: "tool", text: "search(fireflies, query=\"Nord Logistics pricing\")", ms: 690, tokens: 880, cost: 0.0037 },
        { kind: "ok", text: "2 meetings · 14 Aug and 2 Sep", depth: 1 },
        { kind: "tool", text: "search(onedrive, query=\"Nord Logistics offer\")", ms: 1120, tokens: 1540, cost: 0.0064 },
        { kind: "warn", text: "1 document skipped — permission denied", depth: 1 },
        { kind: "answer", text: "Agreed €4.20/unit for 10k+ volumes, valid until December. Rate review due in November.", ms: 640, tokens: 1020, cost: 0.0081, model: "gpt-4o-mini" },
        { kind: "src", text: "[1] meeting 2 Sep · [2] Offer_v3.pdf p.2", depth: 1 },
      ],
    },
    {
      title: "social-agent",
      graph: "content_pipeline",
      lines: [
        { kind: "goal", text: "Post this week's product update to LinkedIn" },
        { kind: "think", text: "Plan: pull changelog, draft in brand voice, schedule", ms: 280, tokens: 520, cost: 0.0021, model: "local/qwen2.5-7b" },
        { kind: "tool", text: "read_changelog(since=\"7d\")", ms: 190, tokens: 340, cost: 0.0004 },
        { kind: "ok", text: "3 shipped features", depth: 1 },
        { kind: "tool", text: "draft_post(tone=\"founder\", length=\"short\")", ms: 1480, tokens: 1260, cost: 0.0015 },
        { kind: "ok", text: "draft ready · 84 words", depth: 1 },
        { kind: "answer", text: "Scheduled for Tuesday 09:00 — best engagement window for this audience.", ms: 420, tokens: 610, cost: 0.0008, model: "local/qwen2.5-7b" },
        { kind: "src", text: "linkedin.schedule · slack.notify(#marketing)", depth: 1 },
      ],
    },
  ],

  founder: {
    name: "Augustinas Žinys",
    role: "Founder & Lead AI/ML Engineer",
    bio: "Zinify Labs was founded by Augustinas — an AI/ML engineer with 8+ years of building things that actually work in production. He studied Embedded Systems (MSc) and Electrical Engineering (BEng, First Class Honours), led engineering teams at scale-ups, and shipped AI products used by thousands. Zinify Labs brings that same hands-on, production-grade approach to every client engagement.",
    highlights: [
      "Led 5-person ML squads at scale-ups",
      "3k+ users on a self-funded AI product",
      "IEEE published researcher",
      "8+ years production ML experience",
    ],
  },

  nav: {
    links: [
      { label: "Work", href: "#projects" },
      { label: "What We Build", href: "#services" },
      { label: "How We Work", href: "#process" },
      { label: "About", href: "#about" },
    ],
  },

  // ── Services ────────────────────────────────────────────
  // `outcome` is the buyer-facing promise and leads the card.
  // `description` explains how. Tags are for technical evaluators.
  // `rank` decides both order and which three render as featured cards.
  services: [
    {
      icon: "◈",
      title: "Agentic AI Systems",
      outcome: "Hand the repetitive, multi-step work to an agent that actually finishes it.",
      description:
        "Not a chatbot that answers — an agent that plans, calls your tools, checks its own work and reports back. Built on LangGraph with explicit state, retries and an audit trail for every step it took.",
      tags: ["LangGraph", "LLM Agents", "Tool Use", "Evals"],
      audience: ["scaleup", "founder"] as Audience[],
      rank: { all: 1, scaleup: 1, founder: 2 } as Rank,
    },
    {
      icon: "⊟",
      title: "Agentic Integrations",
      outcome: "Your agent does the work inside your real systems, not in a sandbox.",
      description:
        "We wire agents into Slack, Gmail, Google Drive, OneDrive, Teams and LinkedIn with scoped permissions and a full action log — so output lands where your team already works, and you can see what it touched.",
      tags: ["Slack", "Gmail", "Google Drive", "OneDrive", "LinkedIn API"],
      audience: ["scaleup"] as Audience[],
      rank: { all: 3, scaleup: 2, founder: 9 } as Rank,
    },
    {
      icon: "◎",
      title: "RAG & Knowledge Systems",
      outcome: "Stop paying your team to hunt for answers that already exist.",
      description:
        "Grounded search across your drives, chat and meeting notes that answers with citations instead of guesses — with an eval set, so you can prove the accuracy before you trust it in front of customers.",
      tags: ["RAG", "Vector DBs", "Embeddings", "Citations"],
      audience: ["scaleup"] as Audience[],
      rank: { all: 2, scaleup: 3, founder: 8 } as Rank,
    },
    {
      icon: "⊞",
      title: "0 → 1 AI Product Build",
      outcome: "From idea to something users can actually pay for — in weeks, not quarters.",
      description:
        "Product, interface, API and model layer built as one thing: React and FastAPI, auth, billing and a data layer that won't need ripping out at your first thousand users.",
      tags: ["React", "FastAPI", "TypeScript", "Billing"],
      audience: ["founder"] as Audience[],
      rank: { all: 4, scaleup: 7, founder: 1 } as Rank,
    },
    {
      icon: "◉",
      title: "Generative AI — Image & Video",
      outcome: "Generative media that stays up, and on budget, under real traffic.",
      description:
        "ComfyUI and diffusion pipelines in production: queueing, GPU autoscaling, cost per generation, and the failure handling a demo never needs and a paying user always finds.",
      tags: ["ComfyUI", "Diffusion", "Video Gen", "GPU Autoscaling"],
      audience: ["founder"] as Audience[],
      rank: { all: 6, scaleup: 6, founder: 3 } as Rank,
    },
    {
      icon: "◐",
      title: "LLM Pipelines & Self-Hosting",
      outcome: "Spend less per request without giving up quality — or your data.",
      description:
        "Gemini, Claude, GPT or open weights on vLLM, chosen against your actual traffic rather than a benchmark. We measure cost and latency per request, then route accordingly.",
      tags: ["Gemini", "Claude", "vLLM", "Open Weights"],
      audience: ["scaleup", "founder"] as Audience[],
      rank: { all: 5, scaleup: 4, founder: 4 } as Rank,
    },
    {
      icon: "⊿",
      title: "Classical ML & Forecasting",
      outcome: "Not every problem needs an LLM — and the ones that don't are cheaper to run.",
      description:
        "Forecasting, churn and risk scoring, anomaly detection, ranking and recommendation. Gradient boosting and time-series models are faster, cost a fraction per prediction, and are far easier to explain to a regulator than a language model. We will tell you when that is the right answer.",
      tags: ["Scikit-Learn", "XGBoost", "LightGBM", "Time Series", "Forecasting"],
      audience: ["scaleup", "founder"] as Audience[],
      rank: { all: 8, scaleup: 8, founder: 10 } as Rank,
    },
    {
      icon: "⊕",
      title: "ML Pipelines & MLOps",
      outcome: "Ship models on a schedule instead of hoping they still work.",
      description:
        "Training, serving, GPU infrastructure, evals and monitoring — the unglamorous layer that decides whether an AI feature survives its first month with real users on it.",
      tags: ["vLLM", "MLflow", "Kubernetes", "Monitoring"],
      audience: ["scaleup"] as Audience[],
      rank: { all: 7, scaleup: 5, founder: 7 } as Rank,
    },
  ],

  process: [
    {
      step: "01",
      title: "Let's talk it through",
      description:
        "An honest conversation about your problem, your existing systems, and what success actually looks like. No jargon, no assumptions.",
      deliverable: "A straight answer on whether this is worth building",
    },
    {
      step: "02",
      title: "We map out the plan",
      description:
        "Before a line of code: data flows, model choices, infrastructure, integration points — and what each request will cost you to serve.",
      deliverable: "System design + cost model + fixed-price build quote",
    },
    {
      step: "03",
      title: "Building it together",
      description:
        "Weekly demos, a shared channel, and code in your repository from week one. You'll never wonder what's happening — you'll see it running.",
      deliverable: "Working software every week, in your stack",
    },
    {
      step: "04",
      title: "Launch & stick around",
      description:
        "We ship to production with monitoring, evals and documentation — then stay, because the real work usually starts the week after launch.",
      deliverable: "Runbook, monitoring, hand-over — and us on call",
    },
  ],

  // ── Engagement models ───────────────────────────────────
  // Scope framing, so "studio" doesn't invite an open-ended debate.
  engagements: [
    {
      name: "Discovery sprint",
      length: "1–2 weeks",
      terms: "Fixed fee",
      outcome: "A design and a build plan you could hand to anyone — including someone else.",
      includes: [
        "Architecture and data-flow design",
        "Model + infra choice, with cost per request",
        "Risk register and the parts we'd cut",
        "Fixed-price quote for the build",
      ],
    },
    {
      name: "Production build",
      length: "6–12 weeks",
      terms: "Fixed scope, weekly demos",
      outcome: "A working system in your infrastructure — monitored, documented and handed over.",
      includes: [
        "The agent, RAG pipeline or product itself",
        "Integrations into your existing stack",
        "Eval suite and production monitoring",
        "Runbook and engineer-to-engineer hand-over",
      ],
      featured: true,
    },
    {
      name: "Embedded team",
      length: "Rolling monthly",
      terms: "From half an FTE",
      outcome: "We sit inside your team and keep shipping — roadmap, iteration and on-call included.",
      includes: [
        "Named engineers, not a rotating bench",
        "Your board, your standups, your repo",
        "Ongoing model and cost tuning",
        "On-call for everything we built",
      ],
    },
  ],

  techStack: {
    "AI / ML": ["Python", "LangGraph", "LangChain", "PyTorch", "OpenCV", "MLflow"],
    "Classical ML": ["Scikit-Learn", "XGBoost", "LightGBM", "Time Series", "Forecasting", "Clustering", "Feature Engineering", "Pandas", "NumPy"],
    "LLM & Agents": ["Gemini", "Claude", "GPT-4o", "Llama", "Mistral", "vLLM", "RAG", "Fine-tuning"],
    "Backend": ["FastAPI", "Django", "Node.js", "REST", "gRPC"],
    "Frontend": ["React", "TypeScript", "Next.js"],
    "Integrations": ["Slack", "Gmail", "Google Drive", "OneDrive", "LinkedIn API", "Facebook API"],
    "Infrastructure": ["Docker", "Kubernetes", "vLLM", "AWS", "GCP", "Seldon"],
  },

  // ── Work ────────────────────────────────────────────────
  // Live products lead. Sunset projects are framed around the capability they
  // prove, not the wind-down. `audience` + `rank` drive the track filter, so an
  // enterprise buyer on the scale-up track never lands on consumer work.
  projects: [
    {
      title: "OpGate.ai",
      description:
        "A supply-chain platform for procurement teams. We built its AI engine — a LangGraph service that analyses every supplier: risk scoring, cited web research, recommendations, and a chat analyst.",
      challenge:
        "Thousands of suppliers, and no fast way to see which one is sole-source, sanctioned, in a conflict region, or slipping on delivery.",
      outcome:
        "An engine that risk-scores every supplier, backs it with cited web research, and recommends the best fit per component with a written rationale.",
      tags: ["LangGraph", "FastAPI", "Risk Scoring", "Web Research", "Teams Bot", "PostgreSQL", "JWT"],
      category: "Agentic AI · Supply Chain",
      metric: "Client platform",
      link: "https://opgate.ai",
      logo: "/logo-opgate.svg",
      status: "live" as const,
      audience: ["scaleup"] as Audience[],
      rank: { all: 1, scaleup: 1, founder: 5 } as Rank,
    },
    {
      title: "Tommy",
      description:
        "A personal AI assistant that actually remembers things — connecting OneDrive, Slack, and Fireflies meeting notes into one place your team can ask questions and get grounded answers.",
      challenge:
        "Teams lose hours hunting across drives, chat, and meeting notes for answers that already exist somewhere.",
      outcome:
        "A single RAG-powered assistant that searches every connected source and answers with citations — live and in daily use.",
      tags: ["RAG", "OneDrive API", "Slack", "Fireflies", "FastAPI", "React", "LangGraph"],
      category: "RAG · Internal Knowledge",
      metric: "Live product",
      link: "https://tommy.lt/en/",
      logo: "/logo-tommy.svg",
      status: "live" as const,
      audience: ["scaleup"] as Audience[],
      rank: { all: 2, scaleup: 2, founder: 4 } as Rank,
    },
    {
      title: "Zinify.ai",
      description:
        "Our self-funded agentic product: an AI agent automating LinkedIn & Facebook content and social selling for e-commerce brands. We grew it to 3,000 users and €1k MRR — proof we can build, ship, and scale an agent to a real audience.",
      challenge:
        "Small e-commerce brands need a constant social presence but have no time or budget for a content team.",
      outcome:
        "An autonomous agent that plans, writes, and posts to LinkedIn & Facebook — scaled to 3,000 users at €1k MRR, on infrastructure we paid for ourselves.",
      tags: ["LLM Agents", "LinkedIn API", "Facebook API", "FastAPI", "LangGraph", "Django"],
      category: "Agentic AI · Our Own Product",
      metric: "3,000 users · €1k MRR",
      link: "https://www.linkedin.com/company/zinify-ai/",
      logo: "/logo-zinify.jpg",
      status: "sunset" as const,
      audience: ["scaleup", "founder"] as Audience[],
      rank: { all: 3, scaleup: 3, founder: 1 } as Rank,
    },
    {
      title: "Aispect.ai",
      description:
        "A computer-vision startup we co-founded: a no-code platform where engineering teams solve vision tasks — object detection, OCR, counting, anomaly detection — just by prompting, with fine-tuning on custom datasets.",
      challenge:
        "Every custom vision model — detection, OCR, counting, anomaly checks — needed ML specialists and weeks of work before a team could even test the idea.",
      outcome:
        "A no-code platform where teams describe the task in a prompt and fine-tune on their own data, taken from idea to a piloted MVP.",
      tags: ["Computer Vision", "No-Code Platform", "Fine-Tuning", "OCR", "FastAPI"],
      category: "Computer Vision · 0 → 1",
      metric: "Co-founded · MVP piloted",
      link: "https://aispect.ai",
      logo: "/logo-aispect.png",
      status: "sunset" as const,
      audience: ["founder"] as Audience[],
      rank: { all: 4, scaleup: 5, founder: 3 } as Rank,
    },
    {
      title: "MyPlayboo",
      description:
        "A subscription generative-media platform, built end to end. The hard part was never the model — it was ComfyUI pipelines on autoscaling GPUs, a billing system, and knowing the cost of every single generation.",
      challenge:
        "Generative media is easy to demo and brutally hard to run reliably, and profitably, for paying users at scale.",
      outcome:
        "A production platform with queued ComfyUI pipelines, subscription billing and GPU infrastructure that serves live traffic without falling over.",
      tags: ["Gen AI", "ComfyUI", "React", "Diffusion", "FastAPI", "Stripe"],
      category: "Gen AI · Consumer (18+)",
      metric: "Live product",
      link: "https://myplayboo.com",
      logo: "/logo-myplayboo.svg",
      status: "live" as const,
      audience: ["founder"] as Audience[],
      rank: { all: 6, scaleup: 9, founder: 2 } as Rank,
    },
    {
      title: "Emotional Parrot",
      description:
        "A fun side project — a parrot that detects your emotions in real time using your webcam and mic. A custom ML pipeline combining computer vision and audio perception.",
      challenge:
        "Reading emotion reliably takes more than a face — expression and voice have to be fused in real time on an ordinary laptop.",
      outcome:
        "A custom pipeline combining webcam vision and audio perception that reacts live, shared as open source.",
      tags: ["OpenCV", "PyTorch", "Audio ML", "Computer Vision"],
      category: "Computer Vision · Open Source",
      metric: "Open source",
      logo: "/logo-emotional-parrot.png",
      status: "fun" as const,
      audience: ["scaleup", "founder"] as Audience[],
      rank: { all: 5, scaleup: 7, founder: 6 } as Rank,
    },
  ],

  // ── Contact / triage ────────────────────────────────────
  contact: {
    heading: "Tell us what you're shipping.",
    sub: "Four taps and a line of context. You'll get a real answer from the engineer who'd do the work — not a discovery call to qualify you.",
    // OPTIONAL — paste a form endpoint (Formspree, Web3Forms, your own API) and
    // the brief is submitted without the visitor ever leaving the page.
    // Leave empty and the brief is composed into a pre-filled email instead.
    formEndpoint: "",
    // OPTIONAL — a Cal.com / Calendly link. Empty hides the booking button.
    callUrl: "",
    responseTime: "Replies within one working day",
  },

  // The scroll-driven pain story (src/components/PainStory). Every line ≤ 32
  // chars. Sources were checked on their primary pages on 2026-10-21:
  //  - Glance, "75% of consumers left frustrated by AI customer service",
  //    national survey of 600+ U.S. consumers, published 2025-12-17.
  //  - Moffatt v. Air Canada, 2024 BCCRT 149 (BC Civil Resolution Tribunal,
  //    2024-02-14): the airline was held liable for its chatbot's wrong refund info.
  //  - LangChain, State of Agent Engineering: 1,340 responses (Nov 18–Dec 2,
  //    2025); quality is the top blocker (32%), latency second (20%).
  // Gartner research is deliberately not quoted: its Content Compliance
  // Policy requires prior written approval for external use.
  pain: {
    label: "Agents in production",
    heading: "Where AI agents break",
    skip: "Skip the story",
    beats: [
      {
        id: "stakes",
        lines: ["75% of consumers had a fast", "AI reply that still frustrated."],
        source: { text: "Glance survey, 600+ U.S. consumers, Dec 2025", href: "https://www.glance.cx/blog/75-of-consumers-left-frustrated-by-ai-customer-service" },
      },
      {
        id: "madeup",
        title: "It makes things up",
        lines: ["Confident answers. No sources."],
        caseLines: ["Air Canada's chatbot got a", "refund rule wrong. It paid."],
        source: { text: "Moffatt v. Air Canada, 2024 BCCRT 149", href: "https://www.canlii.org/en/bc/bccrt/doc/2024/2024bccrt149/2024bccrt149.html" },
      },
      {
        id: "breaks",
        title: "It breaks under real use",
        items: ["Slow to answer", "Leaks private data", "Can't reach your systems", "Waits to be asked"],
        lines: ["Quality is blocker #1 (32%).", "Latency is #2 (20%)."],
        source: { text: "LangChain, State of Agent Engineering", href: "https://www.langchain.com/state-of-agent-engineering" },
      },
      {
        id: "handoff",
        title: "That's where we come in.",
        lines: ["Cited answers. Fast replies.", "Private, connected, proactive."],
      },
    ],
    cta: "Start a project",
    next: "See the work",
  },


  // The triage questions (P1). Answers are compiled into a structured brief.
  brief: {
    steps: [
      {
        id: "building",
        question: "What are you shipping?",
        options: [
          "Agentic workflow",
          "RAG / knowledge search",
          "Generative media",
          "0 → 1 AI product",
          "Not sure yet",
        ],
      },
      {
        id: "stage",
        question: "Where is it today?",
        options: ["An idea", "A prototype", "In production", "Scaling and creaking"],
      },
      {
        id: "timeline",
        question: "When does it need to be live?",
        options: ["Under a month", "This quarter", "Next 6 months", "Just exploring"],
      },
      {
        id: "budget",
        question: "Budget band",
        optional: true,
        options: ["Under €10k", "€10–30k", "€30–75k", "€75k+", "Rather not say"],
      },
    ],
  },

  // Honesty switch for the hero trace. While `measured` is false the trace
  // labels itself an example run. Set it true ONLY when the span timings in
  // `heroDemo` are real, defensible measurements from a live system.
  proof: {
    measured: false,
    traceLabel: "Example run",
    traceLabelMeasured: "Live trace",
  },

  // ⚠️ PLACEHOLDER TESTIMONIALS — replace every quote below with a REAL,
  // client-approved one before deploying. Do not ship invented quotes
  // attributed to real people or companies. Set to [] to hide the section.
  testimonials: [
    {
      quote:
        "[Placeholder — replace with a real quote] They didn't just advise; they shipped a working agent into our stack and stuck around until it was reliable.",
      name: "[Name]",
      role: "CTO",
      company: "ScaleTech",
    },
    {
      quote:
        "[Placeholder — replace with a real quote] Clear weekly demos and zero black boxes. We always knew exactly where the project stood.",
      name: "[Name]",
      role: "Head of Engineering",
      company: "Agmis",
    },
    {
      quote:
        "[Placeholder — replace with a real quote] Deep ML expertise paired with the discipline to actually get it to production. Rare combination.",
      name: "[Name]",
      role: "Founder",
      company: "Blackswan Space",
    },
  ],

  clientsLabel: "Trusted by",

  // Logos are taken from each company's own site (Rocket Software's from
  // Wikipedia — their site blocks automated fetches) and rendered in white.
  // `logoHeight` is in px and balances wide wordmarks against compact marks.
  clients: [
    { name: "Kilo Group", role: "Client", logo: "/clients/kilo.svg", logoHeight: 24 },
    { name: "ScaleTech", role: "CTO", logo: "/clients/scaletech.svg", logoHeight: 14 },
    { name: "Agmis", role: "Enterprise Client", logo: "/clients/agmis.png", logoHeight: 22 },
    { name: "CTO2b", role: "CTO Advisory", logo: "/clients/cto2b.svg", logoHeight: 30 },
    { name: "Blackswan Space", role: "Client", logo: "/clients/blackswan-space.svg", logoHeight: 17 },
    { name: "Rocket Software", role: "Client", logo: "/clients/rocket-software.png", logoHeight: 28 },
    { name: "Prodrive Technologies", role: "Client", logo: "/clients/prodrive-technologies.svg", logoHeight: 25 },
  ],

  social: {
    linkedin: "https://www.linkedin.com/in/augustinas-zinys-9bb05010b/",
    github: "",
  },
};

export type DemoLineKind = "goal" | "think" | "tool" | "ok" | "warn" | "answer" | "src";

/** One span in an agent run. Everything past `text` is optional, so a
 *  scenario can be authored without numbers and instrumented later. */
export type DemoSpan = {
  kind: DemoLineKind;
  text: string;
  ms?: number;      // wall clock for this span → duration column + waterfall
  tokens?: number;  // prompt + completion attributed to this span
  cost?: number;    // USD
  model?: string;
  depth?: number;   // 0..2, span-tree indent
};
export type DemoLine = DemoSpan;
export type DemoScenario = {
  title: string;
  graph?: string;
  lines: DemoSpan[];
  totals?: { ms: number; tokens: number; cost: number; spans: number };
};



export type Service = {
  icon: string;
  title: string;
  outcome: string;
  description: string;
  tags: string[];
  audience: Audience[];
  rank: Rank;
};
export type ProcessStep = (typeof brand.process)[0];
export type Engagement = (typeof brand.engagements)[0];
export type BriefStep = { id: string; question: string; options: string[]; optional?: boolean };
export type Project = {
  title: string;
  description: string;
  challenge?: string;
  outcome?: string;
  tags: string[];
  category: string;
  metric?: string;
  link?: string;
  logo?: string;
  status?: "live" | "sunset" | "fun";
  audience: Audience[];
  rank: Rank;
};
export type Testimonial = (typeof brand.testimonials)[0];
export type Client = (typeof brand.clients)[0];

/** Filter + order any ranked list for the selected track. */
export function forAudience<T extends { audience: Audience[]; rank: Rank }>(
  items: T[],
  audience: Audience
): T[] {
  return items
    .filter((item) => audience === "all" || item.audience.includes(audience))
    .sort((a, b) => a.rank[audience] - b.rank[audience]);
}
